/**
 * Floating horizontal toolbar rendered above the canvas when an element frame
 * is selected. Provides per-element-type editing controls (asset, color, QR,
 * animation/timeline) in a Canva-style bar.
 */

import { useRef, useState, useCallback, useEffect, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import type { Asset, Template } from '@/types/api';

import { asRecord, findAssetForFile, type AssetFileShape } from './elementMerge';
import { getFormatElementTypeDisplay } from './formatElementTypeConfig';

// ─── helpers ──────────────────────────────────────────────────────────────────

const BACKGROUND_ELEMENT_NAME = 'background';
const BACKGROUND_TYPE_OPTIONS = ['color_fill', 'image', 'video'] as const;
type BackgroundElementType = (typeof BACKGROUND_TYPE_OPTIONS)[number];

function isBackgroundName(name: string): boolean {
  return name.trim().toLowerCase() === BACKGROUND_ELEMENT_NAME;
}

function secondsFromMs(ms: number | undefined | null): string {
  if (ms == null || typeof ms !== 'number') return '';
  return String(ms / 1000);
}

function parseSec(raw: string): number | null {
  const t = raw.trim();
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 1000);
}

function setVisMs(el: Record<string, unknown>, key: 'start_ms' | 'end_ms', ms: number | null) {
  const anim: Record<string, unknown> = { ...asRecord(el.animation) };
  const vis: Record<string, unknown> = { ...asRecord(anim.visibility) };
  if (ms == null) delete vis[key];
  else vis[key] = ms;
  if (Object.keys(vis).length === 0) delete anim.visibility;
  else anim.visibility = vis;
  if (Object.keys(anim).length === 0) delete el.animation;
  else el.animation = anim;
}

function setTrimMs(el: Record<string, unknown>, key: 'start_ms' | 'end_ms', ms: number | null) {
  const d = asRecord(el.element_data) ?? {};
  const ve: Record<string, unknown> = { ...asRecord(d.video_element) };
  const trim: Record<string, unknown> = { ...asRecord(ve.video_trim) };
  if (ms == null) delete trim[key];
  else trim[key] = ms;
  if (Object.keys(trim).length === 0) delete ve.video_trim;
  else ve.video_trim = trim;
  d.video_element = ve;
  el.element_data = d;
}

function defaultBgData(t: BackgroundElementType): Record<string, unknown> {
  switch (t) {
    case 'color_fill':
      return { color_fill_element: { element_type: 'color_fill', color: '#0b1218' } };
    case 'image':
      return { image_element: { element_type: 'image', opacity: 1, asset_file: null } };
    case 'video':
      return { video_element: { element_type: 'video', opacity: 1, asset_file: null } };
    default:
      return {};
  }
}

// ─── sub-components ───────────────────────────────────────────────────────────

function Divider() {
  return <div className="w-[1px] h-5 bg-white/15 shrink-0" />;
}

function ToolLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[9px] font-bold uppercase tracking-wider text-white/40 shrink-0">
      {children}
    </span>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

export interface ElementControlsToolbarProps {
  selectedIndex: number;
  template: Template;
  workingElements: unknown[];
  assetsList: Asset[];
  patchEl: (index: number, fn: (el: Record<string, unknown>) => void) => void;
  onPickImage: (index: number) => void;
  onPickVideo: (index: number) => void;
  onDeselect: () => void;
}

export function ElementControlsToolbar({
  selectedIndex,
  template,
  workingElements,
  assetsList,
  patchEl,
  onPickImage,
  onPickVideo,
  onDeselect,
}: ElementControlsToolbarProps) {
  const def = asRecord(template.elements[selectedIndex]);
  const row = asRecord(workingElements[selectedIndex]);
  const [animOpen, setAnimOpen] = useState(false);
  const animRef = useRef<HTMLDivElement>(null);
  const animButtonRef = useRef<HTMLButtonElement>(null);
  const [animPos, setAnimPos] = useState<{ top: number; left: number } | null>(null);

  const toggleAnim = useCallback(() => setAnimOpen((o) => !o), []);

  // Close animation popover on outside click
  useEffect(() => {
    if (!animOpen) return;
    function onDoc(e: MouseEvent) {
      if (animRef.current && !animRef.current.contains(e.target as Node)) {
        setAnimOpen(false);
      }
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [animOpen]);

  // Anchor popover to button (portal) to avoid clipping under canvas.
  useEffect(() => {
    let frameId: number;
    if (!animOpen) {
      frameId = requestAnimationFrame(() => setAnimPos(null));
      return () => cancelAnimationFrame(frameId);
    }
    function update() {
      const rect = animButtonRef.current?.getBoundingClientRect();
      if (!rect) return;
      setAnimPos({ top: rect.bottom + 8, left: rect.left });
    }
    frameId = requestAnimationFrame(update);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [animOpen]);

  if (!def) return null;

  const typ = String(row?.element_type ?? def.element_type ?? '');
  const name = String(row?.element_name ?? def.element_name ?? `Element ${selectedIndex + 1}`);
  const isBg = isBackgroundName(name);
  const typeDisplay = getFormatElementTypeDisplay(typ);
  const data = asRecord(row?.element_data) ?? {};

  // ── Background type ──
  const bgType: BackgroundElementType | null = isBg
    ? BACKGROUND_TYPE_OPTIONS.includes(typ as BackgroundElementType)
      ? (typ as BackgroundElementType)
      : 'color_fill'
    : null;

  // ── Visibility (animation) ──
  const vis = asRecord(asRecord(row?.animation)?.visibility);
  const vStart = vis?.start_ms as number | undefined;
  const vEnd = vis?.end_ms as number | undefined;
  const visWarning =
    vEnd != null && typeof vEnd === 'number' && (vStart == null || typeof vStart !== 'number');
  const visOrderBad = typeof vStart === 'number' && typeof vEnd === 'number' && vEnd <= vStart;

  // ── Video trim ──
  const videoElData = asRecord(asRecord(data?.video_element));
  const trim = asRecord(videoElData?.video_trim);
  const tStart = trim?.start_ms as number | undefined;
  const tEnd = trim?.end_ms as number | undefined;
  const trimWarning =
    tEnd != null && typeof tEnd === 'number' && (tStart == null || typeof tStart !== 'number');
  const trimOrderBad = typeof tStart === 'number' && typeof tEnd === 'number' && tEnd <= tStart;

  return (
    <div className="flex items-center justify-center px-2 py-2 shrink-0">
      <div
        className="relative flex items-center gap-2 rounded-2xl border border-white/10 bg-black/70 px-3 py-2 shadow-xl backdrop-blur-md overflow-x-auto overflow-y-visible max-w-full"
        style={{ scrollbarWidth: 'none' } as CSSProperties}
      >
        {/* ── Element info ── */}
        <span
          className="material-symbols-outlined shrink-0 text-[17px] leading-none text-white/50"
          title={typeDisplay.label}
          aria-hidden
        >
          {typeDisplay.materialSymbol}
        </span>

        <Divider />

        {/* ── Text: hint only ── */}
        {!isBg && typ === 'text' && (
          <div className="flex items-center gap-1.5 shrink-0">
            <span
              className="material-symbols-outlined text-[15px] leading-none text-primary/80"
              aria-hidden
            >
              edit
            </span>
            <span className="text-[10px] text-white/50 italic">Click frame to edit</span>
          </div>
        )}

        {/* ── Color fill ── */}
        {!isBg && typ === 'color_fill' && (
          <label className="flex items-center gap-1.5 cursor-pointer shrink-0">
            <ToolLabel>Fill</ToolLabel>
            <input
              type="color"
              value={String(asRecord(data.color_fill_element)?.color ?? '#0b1218')}
              onChange={(e) => {
                patchEl(selectedIndex, (el) => {
                  const d = asRecord(el.element_data) ?? {};
                  const cfe = asRecord(d.color_fill_element) ?? {};
                  cfe.color = e.target.value;
                  d.color_fill_element = cfe;
                  el.element_data = d;
                });
              }}
              className="h-6 w-10 cursor-pointer rounded border border-white/20 bg-transparent p-0"
              aria-label="Fill color"
            />
          </label>
        )}

        {/* ── Image asset (non-bg) ── */}
        {!isBg &&
          typ === 'image' &&
          (() => {
            const af = asRecord(asRecord(data.image_element)?.asset_file);
            const has = Boolean(af?.uri);
            const linked =
              has && af?.uri
                ? findAssetForFile(af as unknown as AssetFileShape, assetsList)
                : undefined;
            return (
              <div className="flex items-center gap-1.5 min-w-0 shrink-0">
                <ToolLabel>Image</ToolLabel>
                {has ? (
                  <>
                    <span className="text-[10px] text-white/80 max-w-[140px] truncate">
                      {String(linked?.name ?? af?.name ?? '')}
                    </span>
                    <button
                      type="button"
                      onClick={() => onPickImage(selectedIndex)}
                      className="text-[10px] font-bold text-primary cursor-pointer hover:text-primary/80 shrink-0"
                    >
                      Change
                    </button>
                    <button
                      type="button"
                      aria-label="Clear image"
                      className="text-white/40 hover:text-red-400 cursor-pointer shrink-0"
                      onClick={() => {
                        patchEl(selectedIndex, (el) => {
                          const d = asRecord(el.element_data) ?? {};
                          const ie = asRecord(d.image_element) ?? {};
                          ie.asset_file = null;
                          d.image_element = ie;
                          el.element_data = d;
                        });
                      }}
                    >
                      <span className="material-symbols-outlined text-sm leading-none">delete</span>
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => onPickImage(selectedIndex)}
                    className="text-[10px] font-bold text-primary cursor-pointer hover:text-primary/80 shrink-0"
                  >
                    Pick Image
                  </button>
                )}
              </div>
            );
          })()}

        {/* ── Video asset (non-bg) ── */}
        {!isBg &&
          typ === 'video' &&
          (() => {
            const af = asRecord(asRecord(data.video_element)?.asset_file);
            const has = Boolean(af?.uri);
            const linked =
              has && af?.uri
                ? findAssetForFile(af as unknown as AssetFileShape, assetsList)
                : undefined;
            return (
              <div className="flex items-center gap-1.5 min-w-0 shrink-0">
                <ToolLabel>Video</ToolLabel>
                {has ? (
                  <>
                    <span className="text-[10px] text-white/80 max-w-[140px] truncate">
                      {String(linked?.name ?? af?.name ?? '')}
                    </span>
                    <button
                      type="button"
                      onClick={() => onPickVideo(selectedIndex)}
                      className="text-[10px] font-bold text-primary cursor-pointer hover:text-primary/80 shrink-0"
                    >
                      Change
                    </button>
                    <button
                      type="button"
                      aria-label="Clear video"
                      className="text-white/40 hover:text-red-400 cursor-pointer shrink-0"
                      onClick={() => {
                        patchEl(selectedIndex, (el) => {
                          const d = asRecord(el.element_data) ?? {};
                          const ve = asRecord(d.video_element) ?? {};
                          ve.asset_file = null;
                          d.video_element = ve;
                          el.element_data = d;
                        });
                      }}
                    >
                      <span className="material-symbols-outlined text-sm leading-none">delete</span>
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => onPickVideo(selectedIndex)}
                    className="text-[10px] font-bold text-primary cursor-pointer hover:text-primary/80 shrink-0"
                  >
                    Pick Video
                  </button>
                )}
              </div>
            );
          })()}

        {/* ── QR code URL ── */}
        {!isBg && typ === 'qr_code' && (
          <label className="flex items-center gap-1.5 shrink-0">
            <ToolLabel>URL</ToolLabel>
            <input
              type="url"
              value={String(asRecord(data.qr_element)?.url ?? '')}
              onChange={(e) => {
                patchEl(selectedIndex, (el) => {
                  const d = asRecord(el.element_data) ?? {};
                  const qe = asRecord(d.qr_element) ?? {};
                  qe.url = e.target.value;
                  d.qr_element = qe;
                  el.element_data = d;
                });
              }}
              placeholder="https://…"
              className="w-48 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[11px] text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-primary"
              aria-label="QR code URL"
            />
          </label>
        )}

        {/* ── Background controls ── */}
        {isBg && bgType != null && (
          <>
            <ToolLabel>Background</ToolLabel>
            <select
              className="cursor-pointer rounded-md border border-white/10 bg-black/50 py-1 pl-2 pr-7 text-[11px] font-bold text-white focus:outline-none focus:ring-1 focus:ring-primary shrink-0"
              aria-label="Background type"
              value={bgType}
              onChange={(e) => {
                const next = e.target.value as BackgroundElementType;
                patchEl(selectedIndex, (el) => {
                  el.element_type = next;
                  el.element_data = defaultBgData(next);
                  delete el.placeholder_asset;
                });
              }}
            >
              {BACKGROUND_TYPE_OPTIONS.map((opt) => {
                const d = getFormatElementTypeDisplay(opt);
                return (
                  <option key={opt} value={opt}>
                    {d.label}
                  </option>
                );
              })}
            </select>

            {bgType === 'color_fill' &&
              (() => {
                const c = String(asRecord(data.color_fill_element)?.color ?? '#0b1218');
                return (
                  <label className="flex items-center gap-1.5 cursor-pointer shrink-0">
                    <ToolLabel>Color</ToolLabel>
                    <input
                      type="color"
                      value={c}
                      onChange={(e) => {
                        patchEl(selectedIndex, (el) => {
                          const d = asRecord(el.element_data) ?? {};
                          const cfe = asRecord(d.color_fill_element) ?? {};
                          cfe.color = e.target.value;
                          d.color_fill_element = cfe;
                          el.element_data = d;
                        });
                      }}
                      className="h-6 w-10 cursor-pointer rounded border border-white/20 bg-transparent p-0"
                      aria-label="Background fill color"
                    />
                  </label>
                );
              })()}

            {bgType === 'image' &&
              (() => {
                const af = asRecord(asRecord(data.image_element)?.asset_file);
                const has = Boolean(af?.uri);
                return (
                  <div className="flex items-center gap-1.5 min-w-0 shrink-0">
                    {has ? (
                      <>
                        <span className="text-[10px] text-white/80 max-w-[140px] truncate">
                          {String(af?.name ?? '')}
                        </span>
                        <button
                          type="button"
                          onClick={() => onPickImage(selectedIndex)}
                          className="text-[10px] font-bold text-primary cursor-pointer hover:text-primary/80 shrink-0"
                        >
                          Change
                        </button>
                        <button
                          type="button"
                          aria-label="Clear background image"
                          className="text-white/40 hover:text-red-400 cursor-pointer shrink-0"
                          onClick={() => {
                            patchEl(selectedIndex, (el) => {
                              const d = asRecord(el.element_data) ?? {};
                              const ie = asRecord(d.image_element) ?? {};
                              ie.asset_file = null;
                              d.image_element = ie;
                              el.element_data = d;
                            });
                          }}
                        >
                          <span className="material-symbols-outlined text-sm leading-none">
                            delete
                          </span>
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onPickImage(selectedIndex)}
                        className="text-[10px] font-bold text-primary cursor-pointer hover:text-primary/80 shrink-0"
                      >
                        Pick Image
                      </button>
                    )}
                  </div>
                );
              })()}

            {bgType === 'video' &&
              (() => {
                const af = asRecord(asRecord(data.video_element)?.asset_file);
                const has = Boolean(af?.uri);
                return (
                  <div className="flex items-center gap-1.5 min-w-0 shrink-0">
                    {has ? (
                      <>
                        <span className="text-[10px] text-white/80 max-w-[140px] truncate">
                          {String(af?.name ?? '')}
                        </span>
                        <button
                          type="button"
                          onClick={() => onPickVideo(selectedIndex)}
                          className="text-[10px] font-bold text-primary cursor-pointer hover:text-primary/80 shrink-0"
                        >
                          Change
                        </button>
                        <button
                          type="button"
                          aria-label="Clear background video"
                          className="text-white/40 hover:text-red-400 cursor-pointer shrink-0"
                          onClick={() => {
                            patchEl(selectedIndex, (el) => {
                              const d = asRecord(el.element_data) ?? {};
                              const ve = asRecord(d.video_element) ?? {};
                              ve.asset_file = null;
                              d.video_element = ve;
                              el.element_data = d;
                            });
                          }}
                        >
                          <span className="material-symbols-outlined text-sm leading-none">
                            delete
                          </span>
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onPickVideo(selectedIndex)}
                        className="text-[10px] font-bold text-primary cursor-pointer hover:text-primary/80 shrink-0"
                      >
                        Pick Video
                      </button>
                    )}
                  </div>
                );
              })()}
          </>
        )}

        {/* ── Animation / timeline (non-background only) ── */}
        {!isBg && (
          <>
            <Divider />
            <div ref={animRef} className="relative shrink-0">
              <button
                type="button"
                title="Animation & timeline"
                aria-expanded={animOpen}
                onClick={toggleAnim}
                ref={animButtonRef}
                className={`flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-bold cursor-pointer transition-colors ${
                  animOpen
                    ? 'border-primary/40 bg-primary/10 text-primary'
                    : 'border-white/10 text-white/50 hover:border-white/20 hover:text-white/80'
                }`}
              >
                <span className="material-symbols-outlined text-[15px] leading-none">
                  auto_awesome_motion
                </span>
                <span>Timeline</span>
              </button>

              {animOpen && animPos
                ? createPortal(
                    <div
                      ref={animRef}
                      data-element-controls-popover="true"
                      style={{ top: animPos.top, left: animPos.left }}
                      className="fixed z-[9999] w-[240px] space-y-3 rounded-xl border border-white/10 bg-[#0f0f14] p-3 shadow-2xl"
                    >
                      {/* Visibility */}
                      <div>
                        <p className="mb-1.5 text-[9px] font-bold uppercase tracking-wider text-white/40">
                          Visibility (s)
                        </p>
                        <div className="space-y-1.5">
                          <label className="flex items-center gap-2 text-[10px] text-white/60">
                            <span className="w-8 shrink-0">Start</span>
                            <input
                              type="number"
                              min={0}
                              step="any"
                              placeholder="0"
                              value={secondsFromMs(vStart)}
                              onChange={(e) => {
                                const ms = parseSec(e.target.value);
                                patchEl(selectedIndex, (el) => setVisMs(el, 'start_ms', ms));
                              }}
                              className="min-w-0 flex-1 rounded border border-white/10 bg-white/5 px-2 py-0.5 text-[11px] text-white focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                          </label>
                          <label className="flex items-center gap-2 text-[10px] text-white/60">
                            <span className="w-8 shrink-0">End</span>
                            <input
                              type="number"
                              min={0}
                              step="any"
                              placeholder="∞"
                              value={secondsFromMs(vEnd)}
                              onChange={(e) => {
                                const ms = parseSec(e.target.value);
                                patchEl(selectedIndex, (el) => setVisMs(el, 'end_ms', ms));
                              }}
                              className="min-w-0 flex-1 rounded border border-white/10 bg-white/5 px-2 py-0.5 text-[11px] text-white focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                          </label>
                          {visWarning && (
                            <p className="text-[10px] text-amber-400/90">Set start before end.</p>
                          )}
                          {visOrderBad && (
                            <p className="text-[10px] text-amber-400/90">
                              End must be after start.
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Video trim */}
                      {typ === 'video' && (
                        <div className="border-t border-white/10 pt-2">
                          <p className="mb-1.5 text-[9px] font-bold uppercase tracking-wider text-white/40">
                            Video trim (s)
                          </p>
                          <div className="space-y-1.5">
                            <label className="flex items-center gap-2 text-[10px] text-white/60">
                              <span className="w-8 shrink-0">Start</span>
                              <input
                                type="number"
                                min={0}
                                step="any"
                                placeholder="0"
                                value={secondsFromMs(tStart)}
                                onChange={(e) => {
                                  const ms = parseSec(e.target.value);
                                  patchEl(selectedIndex, (el) => setTrimMs(el, 'start_ms', ms));
                                }}
                                className="min-w-0 flex-1 rounded border border-white/10 bg-white/5 px-2 py-0.5 text-[11px] text-white focus:outline-none focus:ring-1 focus:ring-primary"
                              />
                            </label>
                            <label className="flex items-center gap-2 text-[10px] text-white/60">
                              <span className="w-8 shrink-0">End</span>
                              <input
                                type="number"
                                min={0}
                                step="any"
                                placeholder="∞"
                                value={secondsFromMs(tEnd)}
                                onChange={(e) => {
                                  const ms = parseSec(e.target.value);
                                  patchEl(selectedIndex, (el) => setTrimMs(el, 'end_ms', ms));
                                }}
                                className="min-w-0 flex-1 rounded border border-white/10 bg-white/5 px-2 py-0.5 text-[11px] text-white focus:outline-none focus:ring-1 focus:ring-primary"
                              />
                            </label>
                            {trimWarning && (
                              <p className="text-[10px] text-amber-400/90">
                                Set trim start before end.
                              </p>
                            )}
                            {trimOrderBad && (
                              <p className="text-[10px] text-amber-400/90">
                                Trim end must be after start.
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>,
                    document.body,
                  )
                : null}
            </div>
          </>
        )}

        <Divider />

        {/* ── Deselect ── */}
        <button
          type="button"
          onClick={onDeselect}
          aria-label="Deselect element"
          className="shrink-0 rounded-md p-1 text-white/40 hover:text-white/80 cursor-pointer transition-colors"
        >
          <span className="material-symbols-outlined text-[16px] leading-none">close</span>
        </button>
      </div>
    </div>
  );
}
